import {
    CsAccordion,
    CsModal,
    CsGhostButton,
    CsNotification,
    CsPrimaryButton,
    CsSort,
    MsText,
} from '@sunrise/components'
import { MsArrowRight, MsSettings } from '@sunrise/icons'
import { screenSizeDetection, useGlobalStore } from '@sunrise/shared'
import { Ads, Cms } from '@sunrise/types'
import axios from 'axios'
import { isEmpty } from 'lodash-es'
import { PropType, computed, defineComponent, nextTick, onMounted, reactive, toRefs } from 'vue'
import CsTilesResultList from '../../../../components/CsTilesResultList/CsTilesResultList.vue'
import CsNewsCategory from '../../../../components/filters/CsNewsCategory/CsNewsCategory.vue'
import CsSurroundingArea from '../../../../components/filters/CsSurroundingArea/CsSurroundingArea.vue'
import CsTimeframe from '../../../../components/filters/CsTimeframe/CsTimeframe.vue'
import { SurroundingOptions, TimeframeOptions } from '../../../../components/filters/filter.types'

import { mapToCombinedNewsFeed } from '../../../../ts/mapper/simpleNewsItemMapper'
import { parameterSerializer } from '../../../../ts/utils/parameterSerializer'
import {
    createTrackings,
    mlmTrackViews,
    tealiumTrackFilterModal,
    tealiumTrackNextPage,
    trackSort,
} from '../M_Combined_News_Resultlist.tracking'
import { CombinedNewsFeed, FilterClicks } from '../M_Combined_News_Resultlist.types'
import {
    NewsType,
    NewsRequestParams,
    RadiusExpansionType,
    NewsResponse,
    ClientNewsRequestParams,
} from '../../../../services/newsService/newsService.types'

// const ONE_DAY = 24 * 60 * 60 * 1000
// const TIMEFRAME_OFFSETS = {
//     [TimeframeOptions.YESTERDAY]: ONE_DAY,
//     [TimeframeOptions.LAST_7_DAYS]: 7 * ONE_DAY,
//     [TimeframeOptions.LAST_30_DAYS]: 30 * ONE_DAY,
// }
const sortOptions = [
    {
        label: 'Neueste',
        value: 'Neueste',
    },
    {
        label: 'Meist geklickt',
        value: 'Relevanz',
    },
]
const filterValues = [
    {
        key: NewsType.POLICE,
        label: 'Polizei',
        value: 'Polizei',
    },
    {
        key: NewsType.FIRE,
        label: 'Feuerwehr',
        value: 'Feuerwehr',
    },
    {
        key: NewsType.RSS,
        label: 'RSS',
        value: 'RSS',
    },
]

export default defineComponent({
    name: 'HydratedNewsFeedResultlist',
    components: {
        MsText,
        CsNotification,
        MsArrowRight,
        MsSettings,
        CsAccordion,
        CsModal,
        CsPrimaryButton,
        CsGhostButton,
        CsNewsCategory,
        CsTimeframe,
        CsSurroundingArea,
        CsSort,
        CsTilesResultList,
    },
    props: {
        serviceData: {
            type: Object as PropType<CombinedNewsFeed>,
            required: true,
        },
        allDisturber: {
            type: Array as PropType<Cms.Content[]>,
            default: () => [],
        },
        headline: { type: Object as PropType<string> },
        request: {
            type: Object as PropType<NewsRequestParams>,
            required: true,
        },
        hideImagesOnMobile: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const { isScreenM } = screenSizeDetection()
        const requestParams = reactive<NewsRequestParams>({
            ...props.request,
        })
        const state = reactive({
            selectedSort: sortOptions[0].value,
            selectedCategories: props.request.type || ([] as string[]),
            // FIXME selectedTimeframe: props.request.since || TimeframeOptions.ANY,
            selectedTimeframe: TimeframeOptions.ANY,
            selectedSurroundingArea: SurroundingOptions.REGIONAL,
            renderedItems: props.serviceData.items,
            isDisabled: false,
            isLoading: false,
            hasRadiusExtended: props.serviceData?.radiusExpansion === '25000',
            isFeedEmpty: isEmpty(props.serviceData?.items),
            isLastPage: props.serviceData.isLastPage,
            modalVisible: false,
            searchBoxUsed: false,
        })
        const isFallback = computed(
            () => isCountyFallback.value || isFederalStateFallback.value || isNationFallback.value,
        )
        const isCountyFallback = computed(() => props.serviceData?.radiusExpansionBy === RadiusExpansionType.COUNTY)
        const isFederalStateFallback = computed(
            () => props.serviceData?.radiusExpansionBy === RadiusExpansionType.FEDERAL_STATE,
        )
        const isNationFallback = computed(() => props.serviceData?.radiusExpansionBy === RadiusExpansionType.NATION)
        const fallbackLocationName = computed(() => props.serviceData?.radiusExpansion)
        const showNotification = computed(() => isFallback.value || state.isFeedEmpty)
        const showButton = computed(() => !state.isLastPage && !state.isFeedEmpty)
        const trackings = computed(() => createTrackings(state.renderedItems, props.allDisturber, requestParams.size))
        const computedHideImages = computed(() => props.hideImagesOnMobile && !isScreenM.value)

        onMounted(() => {
            if (state.renderedItems) {
                mlmTrackViews(state.renderedItems, requestParams.page + 1, props.allDisturber)
            }
        })

        async function onClick() {
            if (state.isDisabled || state.isLoading || state.isLastPage) {
                return
            }

            requestParams.page++

            tealiumTrackNextPage()
            loadNews()
        }

        function onFilterClick() {
            state.modalVisible = false
            requestParams.page = 0

            loadNews()
        }

        async function loadNews() {
            state.isLoading = true

            const requestUrl = buildRequestUrl(state.selectedSurroundingArea, requestParams)
            const { data: feed } = await axios.post<NewsResponse>(requestUrl)

            if (!feed) {
                return
            }

            const { page, items, isLastPage } = mapToCombinedNewsFeed(feed)
            requestParams.page = page

            let amountOfRenderedItems = 0
            if (page === 0) {
                state.renderedItems = items
            } else {
                amountOfRenderedItems = state.renderedItems.length
                state.renderedItems = state.renderedItems.concat(items)
            }

            mlmTrackViews(items, requestParams.page + 1, props.allDisturber, amountOfRenderedItems)

            state.isLastPage = isLastPage
            state.isLoading = false

            await nextTick()
            window.refreshAds(Ads.RefreshReason.USER_INTERACTION)
        }

        function openModal() {
            state.modalVisible = true

            tealiumTrackFilterModal(FilterClicks.FILTER_OPEN)
        }

        function closeModal() {
            state.modalVisible = false

            tealiumTrackFilterModal(FilterClicks.FILTER_CLOSE)
        }

        function updateSortOption(value: string) {
            state.selectedSort = value
            state.modalVisible = false
            state.searchBoxUsed = true

            requestParams.sort = mapSortOption(value)
            requestParams.page = 0

            trackSort(value)
            loadNews()
        }

        function onResetModal() {
            requestParams.type = [NewsType.POLICE, NewsType.FIRE, NewsType.RSS]
            // FIXME the current endpoint no longer supports 'since'
            // requestParams.since = TimeframeOptions.ANY

            state.selectedSurroundingArea = SurroundingOptions.REGIONAL
        }

        function onCategoryFilterChange(values: string[]) {
            requestParams.type = typesFrom(values)
            requestParams.page = 0

            loadNews()
        }

        return {
            filterValues,
            isFallback,
            isScreenM,
            showButton,
            showNotification,
            sortOptions,
            trackings,
            onClick,
            openModal,
            closeModal,
            onResetModal,
            onFilterClick,
            onCategoryFilterChange,
            updateSortOption,
            ...toRefs(state),
            computedHideImages,
            isCountyFallback,
            isFederalStateFallback,
            isNationFallback,
            fallbackLocationName,
        }
    },
})

function buildRequestUrl(area: SurroundingOptions, request: NewsRequestParams) {
    const serviceUrlPath = useGlobalStore().urlPaths?.services

    const newsRequestParameters: ClientNewsRequestParams = {
        locationId: locationIdFor(area),
        ...request,
    }

    return `${serviceUrlPath}/news?${parameterSerializer(newsRequestParameters)}`
}

// function sinceFor(timeframe: TimeframeOptions) {
//     if (timeframe === TimeframeOptions.ANY) {
//         return ''
//     }

//     const offset = TIMEFRAME_OFFSETS[timeframe]
//     const sinceDate = new Date(new Date().getTime() - offset)

//     sinceDate.setHours(0)
//     sinceDate.setMinutes(0)
//     sinceDate.setSeconds(0)
//     sinceDate.setMilliseconds(0)

//     const isoString = sinceDate.toISOString()

//     const withoutMilliseconds = isoString.slice(0, isoString.length - 5)

//     return `${withoutMilliseconds}Z`
// }

function typesFrom(types: string[]): NewsType[] {
    if (types.includes('Alle Kategorien')) {
        return [NewsType.POLICE, NewsType.FIRE, NewsType.RSS]
    }

    return types.map((type) => {
        switch (type) {
            case 'Polizei':
                return NewsType.POLICE
            case 'Feuerwehr':
                return NewsType.FIRE
            case 'RSS':
                return NewsType.RSS
            default:
                return NewsType.POLICE
        }
    })
}

function mapSortOption(value: string) {
    switch (value) {
        case 'Relevanz':
            return ['viewCount,desc', 'published,desc']
        case 'Neueste':
        default:
            return []
    }
}

function getFederalState(location: Cms.Location | undefined): Cms.Location | undefined {
    let current: Cms.Location | undefined = location
    let result: Cms.Location | undefined = undefined

    while (current && !result) {
        if (current.type === Cms.LocationType.BUNDESLAND) {
            result = current
        }

        current = current.parent
    }

    return result
}

function locationIdFor(radius: SurroundingOptions) {
    const requestContext = useGlobalStore().requestContext
    let locationId: string | undefined

    if (radius === SurroundingOptions.REGIONAL) {
        locationId = requestContext?.location.id
    } else {
        locationId = getFederalState(requestContext?.location)?.id
    }

    return locationId || 'deutschland'
}
